import ModalWarning from '@/components/ui/custom/modals/ModalWarning';
import { FilterPopup } from '@/components/filter/FilterPopup';
import SelectFilterHeader from '@/components/filter/SelectFilterHeader';
import useHackathon from '@/data/hook/hackathon/useHackathon';
import useWinners from '@/data/hook/hackathon/useWinners';
import useManualQuery from '@/data/hook/query/v1/useManualQuery';
import { winnersQueryKeys } from '@/data/services/querykeys';
import { deleteWinner } from '@/data/services/winnerServices';
import { Project } from '@/models/Project';
import { getWinnerByProjectId, getWinnersCount } from '@/utils/hackathon';
import { useMutation } from '@tanstack/react-query';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import CreateSimpleProjectCard from './factory/CreateSimpleCard';
import useSetWinners from './HackathonWinnersAction.hooks';

type HackathonWinnersProps = {
  project: Project;
  onMutate?: (project: Project) => void;
};

export default function HackathonWinnersAction({
  project,
}: HackathonWinnersProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.card.actions',
  });
  const { t: tPosition } = useTranslation('translation', {
    keyPrefix: 'projects.card.position',
  });

  const {
    setWinner,
    stage,
    isLoading,
    isCommittingStage,
    inStage,
    commitStage,
    clearStage,
  } = useSetWinners({ project });

  const { hackathon } = useHackathon();

  const { winners } = useWinners(hackathon);

  const winner = getWinnerByProjectId(project.id, winners);

  const { removeItem: removeWinner } = useManualQuery(
    winnersQueryKeys.list({
      hackathonId: hackathon?.id,
      pageSize: getWinnersCount(),
    }),
  );

  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation(
    deleteWinner,
    {
      onSuccess: (_, variabes) => {
        removeWinner(variabes);
      },
    },
  );

  const mountPosition = (positions: number[]) => {
    return positions.map(position => ({
      label: tPosition('rank', {
        ordinal: true,
        count: position,
      }),
      value: position,
    }));
  };

  const options = mountPosition([1, 2, 3]);

  const selected = useMemo<string>(() => {
    return (
      options.find(option => option.value === winner?.rank)?.label || t('none')
    );
  }, [options, t, winner?.rank]);

  const onClear = () => winner && deleteMutate(winner.id);

  return (
    <Fragment>
      <FilterPopup
        onClear={winner ? onClear : undefined}
        label={t('rank')}
        reference={
          <SelectFilterHeader
            label={t('rank')}
            selected={selected}
            loading={isLoading || isDeleting}
          />
        }
      >
        <ul className="min-h-6 flex flex-col gap-1">
          {options.map(option => (
            <li
              onClick={() => setWinner(option.value)}
              role="button"
              key={String(option.value)}
              className={twMerge(
                'cursor-pointer flex items-center justify-between gap-6 hover:text-primary transition ease-in-out duration-300 text-14',
                winner?.rank === option.value &&
                  'text-primary pointer-events-none',
              )}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </FilterPopup>
      <ModalWarning
        visible={inStage}
        isLoading={isCommittingStage}
        onClickCancel={clearStage}
        onClickConfirm={commitStage}
        className={{
          title: 'text-error',
          text: 'text-error',
        }}
        objectTarget={
          stage.current?.winnerShow.project && (
            <ul className="pointer-events-none text-left">
              <CreateSimpleProjectCard
                project={stage.current?.winnerShow.project}
              />
            </ul>
          )
        }
        translationString="modalUpdateWinner"
      />
    </Fragment>
  );
}
